<template>
	<div style="text-align: left">
		<h2 id="wf-tour-create-sidebar-title">{{ existingWorkflow ? "Workflow Settings" : "New Workflow" }}</h2>
		<p>{{ existingWorkflow ? "Update this workflows settings" : "Create a new workflow" }}</p>
		<el-tag
			v-if="existingWorkflow && existingWorkflow.workflowId"
			@click.native="copy()"
			style="cursor: pointer"
			type="info"
		>
			Workflow ID: {{ existingWorkflow.workflowId }}
		</el-tag>
		<el-divider></el-divider>
		<el-form
			ref="form"
			@keyup.native.enter="existingWorkflow ? update() : create()"
			:rules="formRules"
			label-position="top"
			label-width="100px"
			:model="form"
		>
			<el-form-item prop="name" label="Workflow Name">
				<el-input id="wf-tour-workflow-name-input" maxlength="50" placeholder="My workflow's name" v-model="form.name"></el-input>
			</el-form-item>
			<el-form-item prop="description" label="Description">
				<el-input
					:show-word-limit="true"
					maxlength="150"
					resize="none"
					type="textarea"
					:autosize="{ minRows: 3, maxRows: 9 }"
					placeholder="..."
					v-model="form.description"
				>
				</el-input>
			</el-form-item>
			<el-form-item v-if="form.workflowMode" prop="mode" label="Workflow Mode">
				<el-radio-group v-model="form.workflowMode">
					<el-radio-button label="blocking"></el-radio-button>
					<el-radio-button label="sequential"></el-radio-button>
				</el-radio-group>
				<p style="font-size: 10px; word-break: break-word; margin: 0px; line-height: 20px; margin-top: 5px">
					About mode:
					{{
						form.workflowMode === "blocking"
							? "Blocking mode restricts a specific integration from executing before all integrations that connect to it have executed"
							: "Sequential mode allows a specific integration to execute as soon as any data has been passed to it from any other integration"
					}}
				</p>
			</el-form-item>
			<el-form-item v-if="typeof form.deactivateOnError === 'boolean'" prop="deactivateOnError">
				<el-switch v-model="form.deactivateOnError" active-text="Deactivate Workflow On Error" inactive-text="">
				</el-switch>
			</el-form-item>
			<el-form-item v-if="typeof form.disableExecutionLogs === 'boolean'" prop="disableExecutionLogs">
				<el-switch
					v-model="form.disableExecutionLogs"
					active-text="Deactivate Execution Logging"
					inactive-text=""
				>
				</el-switch>
			</el-form-item>
			<el-form-item prop="timezone" label="Timezone">
				<el-select
					no-match-text="Timezone not found"
					filterable
					style="width: 100%"
					v-model="form.timezone"
					placeholder="Select timezone"
				>
					<el-option v-for="zone in timezones" :key="zone" :label="zone" :value="zone"> </el-option>
				</el-select>
			</el-form-item>
			<el-form-item>
				<el-button
					id="wf-tour-workflow-create-btn"
					@click="existingWorkflow ? update() : create()"
					:disabled="!form.name"
					style="width: 100%; margin-top: 12px"
					type="primary"
					:loading="operationInProgress"
					>{{ existingWorkflow ? "Update" : "Create" }}</el-button
				>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
import { copyToClipboard } from "../utils/copyToClipboard";
import { timezones } from "../utils/timezones.js";
import { mapActions } from "vuex";
export default {
	props: {
		existingWorkflow: Object,
		workflowItemList: Boolean
	},
	watch: {
		existingWorkflow: {
			handler(val) {
				if (!val) return;
				this.form = JSON.parse(JSON.stringify(val));
			},
			deep: true,
			immediate: true
		}
	},
	data() {
		return {
			operationInProgress: false,
			form: this.existingWorkflow || {
				name: "",
				description: "",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
			formRules: {
				name: [{ required: true, message: "A workflow name is required", trigger: "change" }],
			},
			timezones: timezones,
		};
	},
	methods: {
		...mapActions(["changesToUsageCreditMETHOD", "fetchWorkflowVersionsMETHOD"]),
		copy() {
			copyToClipboard(this.existingWorkflow.workflowId);
			this.$notify({
				title: "Success",
				message: "Workflow id copied to clipboard",
				type: "success",
				position: "bottom-left",
			});
		},
		create() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					this.operationInProgress = true;
					const result = await this.httpHelper("RUNTIME", "POST", "workflow/createOne", this.form);
					setTimeout(() => {
						this.operationInProgress = false;
						this.changesToUsageCreditMETHOD();
						if (!result?.error) {
							this.$emit("operationComplete", result.response.workflow);
							this.form = {
								name: "",
								description: "",
								timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
							};
							this.$notify({
								title: "Success",
								message: "Workflow created",
								type: "success",
								position: "bottom-left",
							});

							this.$store.commit("SET_WORKFLOW_VERSION", {
								workflowId: result.response.workflow.id,
								versionId: result.response.workflow.versionId
							});
						} else {
							if (result.response?.data?.noCredit) {
								this.$notify({
									title: "Warning",
									message: result.response?.data?.error,
									type: "warning",
									position: "bottom-left",
								});
							} else {
								this.$notifyError(this, result.error);
							}
						}
					}, 500);
				} else {
					return;
				}
			});
		},
		update() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					this.operationInProgress = true;
					const workflowId = this.$route.params.workflowId;
					const selectedVersionId = this.$store.getters.getSelectedWorkflowVersion(workflowId);

					const form = {
						...this.form,
						settings: {
							...this.existingWorkflow.settings,
							workflowMode: this.form.workflowMode || this.existingWorkflow.settings.workflowMode,
							deactivateOnError:
								typeof this.form.deactivateOnError === "boolean"
									? this.form.deactivateOnError
									: this.existingWorkflow.settings.deactivateOnError,
							disableExecutionLogs:
								typeof this.form.disableExecutionLogs === "boolean"
									? this.form.disableExecutionLogs
									: this.existingWorkflow.settings.disableExecutionLogs,
							timezone: this.form.timezone,
						},
				        versionId: this.existingWorkflow?.versionId ?? selectedVersionId ?? null
					};

					if(this.workflowItemList){
						const versions = await this.fetchWorkflowVersionsMETHOD(this.existingWorkflow.workflowId);
						const baseVersion = versions?.find(v => v.version === "1.0.0");
						if(baseVersion?.versionId) form.versionId = baseVersion?.versionId;
					}
					
					const result = await this.httpHelper("RUNTIME", "PUT", "workflow/updateOne", form);
					setTimeout(() => {
						this.operationInProgress = false;
						if (!result?.error) {
							this.$emit("operationComplete", result.response.workflow);
							this.form = {
								name: "",
								description: "",
								timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
							};
							this.$notify({
								title: "Success",
								message: "Workflow updated",
								type: "success",
								position: "bottom-left",
							});
						} else {
							this.$notifyError(this, result.error);
						}
					}, 500);
				} else {
					return;
				}
			});
		},
	},
};
</script>

<style></style>
